import { Button, Modal, tokens, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { uniqBy } from 'lodash-es';
import {
  closedModal,
  loadedModal,
  trackClick,
  extractAccountGroupingProps,
} from '@hotjar/tracking';
import { useOwnedAccount } from '@hotjar/account';
import {
  useEffectOnceWithDependencies,
  createGlobalModal,
} from '../../external-dependencies/webapp-utils';
import { ChurnSurvey, ModalHeaderSurvey } from './components';
import type { GlobalModalProps } from '../../external-dependencies/webapp-utils';
import type { ProductAllowance } from './types';

const S = {
  ModalBody: styled(Modal.Body)`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  `,
  ModalBodyInnerV2: styled(Modal.Body)`
    display: grid;
    grid-template-columns: min-content auto;
    column-gap: ${tokens.spacer.x3};
    row-gap: ${tokens.spacer.x7};
    margin: auto;
  `,
  ModalBodyInner: styled.div`
    max-width: 780px;
    width: 100%;
    margin: auto;
  `,
  Heading: styled(Text)`
    margin-bottom: ${tokens.spacer.x3};
  `,
};

interface Props {
  subscriptionSubtotal: string;
  accountId: string;
  allowancesToRemove: ProductAllowance[];
}

const CHURN_FORM_NAME = 'churn-survey';
const MODAL_NAME = 'Change plan confirmation with churn survey';

export function ChurnModal({
  onCancel: onClose,
  onSubmit,
  subscriptionSubtotal,
  accountId,
  allowancesToRemove,
}: Props & GlobalModalProps<void, void>): JSX.Element {
  const { t: tChurn } = useTranslationWithPrefix('checkout:churn');
  const { data: account } = useOwnedAccount();

  useEffectOnceWithDependencies(
    () => {
      const numberOfSites = uniqBy(
        allowancesToRemove,
        (allowance) => allowance.entity.entityIdentifier,
      ).length;
      loadedModal(MODAL_NAME, extractAccountGroupingProps(account), {
        modal_site_count: numberOfSites,
      });
    },
    [account],
    () => account !== undefined,
  );

  function handleClose() {
    closedModal(MODAL_NAME, extractAccountGroupingProps(account));
    onClose();
  }

  function handleSubmit() {
    trackClick({
      name: 'Confirm change',
      uiPosition: `${MODAL_NAME} modal`,
      groupData: extractAccountGroupingProps(account),
    });
    onSubmit();
  }

  return (
    <Modal
      contentLabel={tChurn('surveyv2.confirmation-modal.heading')}
      size="fullscreen"
      isOpen
      onClose={handleClose}
      appElementSelector="#app-container"
    >
      <S.ModalBody>
        <S.ModalBodyInnerV2>
          <ModalHeaderSurvey />
          <ChurnSurvey
            onSubmit={handleSubmit}
            formId={CHURN_FORM_NAME}
            subscriptionSubtotal={subscriptionSubtotal}
            accountId={accountId}
          />
        </S.ModalBodyInnerV2>
      </S.ModalBody>
      <Modal.Footer>
        <Button kind="danger" form={CHURN_FORM_NAME} type="submit">
          {tChurn('confirmation-modal.actions.continue-to-downgrade')}
        </Button>
        <Button kind="ghost" onClick={handleClose}>
          {tChurn('confirmation-modal.actions.keep-my-plans')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export const showChurnSurveyModal = createGlobalModal<Props, void, void>(ChurnModal);
